import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Star, MapPin, BadgeCheck, ArrowRight, MessageSquareQuote, Languages } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";

/* ---------- deterministic pseudo-random generator ---------- */
function mulberry32(a) {
    return function () {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

const FIRST = [
    "Aarav", "Vivaan", "Aditya", "Arjun", "Sai", "Reyansh", "Krishna", "Ishaan", "Rohan", "Kabir",
    "Ananya", "Diya", "Aadhya", "Myra", "Sara", "Ira", "Priya", "Kavya", "Anika", "Navya",
    "Rahul", "Amit", "Sneha", "Pooja", "Vikram", "Karan", "Neha", "Riya", "Manish", "Suresh",
    "Divya", "Tanvi", "Nikhil", "Shreya", "Varun", "Meera", "Arnav", "Ishita", "Aditi", "Rajesh",
    "Kunal", "Swati", "Deepak", "Simran", "Harish", "Nisha", "Gaurav", "Ritika", "Mohit", "Farhan",
];
const LAST = [
    "Sharma", "Verma", "Patel", "Gupta", "Mehta", "Shah", "Reddy", "Iyer", "Nair", "Singh",
    "Chauhan", "Joshi", "Kulkarni", "Deshmukh", "Agarwal", "Bansal", "Chopra", "Malhotra", "Kapoor", "Bhatia",
    "Yadav", "Pandey", "Mishra", "Tiwari", "Dubey", "Rao", "Pillai", "Menon", "Das", "Bose",
];
const BUSINESSES = [
    "Real Estate", "Coaching Institute", "Dental Clinic", "Travel Agency", "Jewellery Store",
    "Fitness Studio", "Car Dealership", "Interior Design Firm", "CA Firm", "Boutique",
    "Restaurant", "Event Management", "Logistics Company", "Solar Solutions", "Beauty Salon",
    "Electronics Retail", "Architecture Firm", "Wedding Planning", "Pharma Distribution", "Import-Export Business",
];

/* ---------- regional reviews: every region writes in its own language ---------- */
const REGIONS = [
    {
        lang: "Gujarati", native: "ગુજરાતી",
        cities: ["Ahmedabad", "Surat", "Rajkot", "Vadodara"],
        t5: [
            { n: "WhatsApp પર ગ્રાહકોના જવાબ હવે AI તરત આપે છે. અમારી દુકાનની inquiry એક પણ miss થતી નથી.", e: "AI now replies to customers instantly on WhatsApp. Not a single enquiry from our shop gets missed." },
            { n: "હું દુકાને વ્યસ્ત હોઉં ત્યારે પણ AI ગ્રાહકો સાથે વાત કરે છે. ખૂબ જ સરસ સેવા.", e: "Even when I'm busy at the shop, the AI talks to customers. Truly excellent service." },
            { n: "ફોલો-અપ હવે આપોઆપ થાય છે. ગ્રાહકો વધી ગયા છે અને અમારો સમય બચી ગયો.", e: "Follow-ups now happen automatically. Customers have grown and our time is saved." },
        ],
        t4: [
            { n: "શરૂઆતમાં set-up માં થોડો સમય લાગ્યો, પણ હવે બધું સારી રીતે ચાલે છે.", e: "Setup took a little time at first, but everything runs smoothly now." },
            { n: "સારો પ્રોડક્ટ છે. ક્યારેક જટિલ સવાલોમાં human જરૂરી પડે છે.", e: "Good product. Sometimes complex questions still need a human." },
        ],
        t3: [{ n: "સામાન્ય પ્રશ્નો માટે સારું છે. વધુ features જોઈએ.", e: "Good for basic questions. We'd like more features." }],
    },
    {
        lang: "Tamil", native: "தமிழ்",
        cities: ["Chennai", "Coimbatore"],
        t5: [
            { n: "எங்கள் கடையின் WhatsApp கேள்விகளுக்கு AI உடனே பதில் அளிக்கிறது. வாடிக்கையாளர்கள் மிகவும் மகிழ்ச்சியாக உள்ளனர்.", e: "AI instantly answers our shop's WhatsApp enquiries. Customers are very happy." },
            { n: "இரவு நேரத்திலும் விசாரணைகள் தவறவில்லை. முன்பை விட முன்பதிவுகள் அதிகரித்துள்ளன.", e: "No enquiry is missed even at night. Bookings have increased compared to before." },
            { n: "AI அழைப்பு மிகவும் இயல்பாக உள்ளது — வாடிக்கையாளர்களுக்கு வித்தியாசமே தெரியவில்லை.", e: "The AI call sounds very natural — customers can't even tell the difference." },
        ],
        t4: [
            { n: "நல்ல சேவை. சில சிக்கலான கேள்விகளுக்கு மனிதர் தேவை.", e: "Good service. A few complex questions still need a human." },
            { n: "மதிப்புக்கு ஏற்ற தயாரிப்பு. அறிக்கைகள் இன்னும் சிறப்பாக இருக்கலாம்.", e: "Value for money. Reports could be even better." },
        ],
        t3: [{ n: "அடிப்படை விசாரணைகளுக்கு நன்றாக வேலை செய்கிறது.", e: "Works well for basic enquiries." }],
    },
    {
        lang: "Telugu", native: "తెలుగు",
        cities: ["Hyderabad"],
        t5: [
            { n: "మా దుకాణం మూసివేసిన తర్వాత కూడా AI కస్టమర్ ప్రశ్నలకు సమాధానం ఇస్తుంది.", e: "Even after our shop closes, the AI answers customer questions." },
            { n: "WhatsApp సమాధానాలు తక్షణమే వస్తాయి. లీడ్స్ ఒక్కటీ మిస్ అవ్వటం లేదు.", e: "WhatsApp replies come instantly. Not a single lead is missed." },
            { n: "ఫాలో-అప్పులు ఆటోమేటిక్ గా జరుగుతాయి — మా బుకింగ్స్ పెరిగాయి.", e: "Follow-ups happen automatically — our bookings have grown." },
        ],
        t4: [
            { n: "మంచి ప్రోడక్ట్. సెటప్ కొంచెం సమయం తీసుకుంది.", e: "Good product. Setup took a little time." },
            { n: "బాగుంది. కొన్ని కష్టమైన ప్రశ్నలకు మనిషి అవసరం.", e: "It's good. Some difficult questions need a person." },
        ],
        t3: [{ n: "ప్రాథమిక అవసరాలకు సరిపోతుంది.", e: "Suffices for basic needs." }],
    },
    {
        lang: "Punjabi", native: "ਪੰਜਾਬੀ",
        cities: ["Ludhiana", "Amritsar", "Chandigarh"],
        t5: [
            { n: "ਸਾਡੀ ਦੁਕਾਨ ਬੰਦ ਹੋਣ ਤੋਂ ਬਾਅਦ ਵੀ AI ਗਾਹਕਾਂ ਨਾਲ ਗੱਲ ਕਰਦਾ ਹੈ। ਬਹੁਤ ਵਧੀਆ!", e: "Even after our shop closes, the AI talks to customers. Excellent!" },
            { n: "WhatsApp ਉੱਤੇ ਹਰ ਸਵਾਲ ਦਾ ਤੁਰੰਤ ਜਵਾਬ ਮਿਲਦਾ ਹੈ। bookings ਵਧ ਗਈਆਂ ਹਨ।", e: "Every question on WhatsApp gets an instant reply. Bookings have increased." },
            { n: "ਫਾਲੋ-ਅੱਪ ਹੁਣ ਆਪਣੇ ਆਪ ਹੁੰਦੇ ਹਨ। staff ਤੋਂ ਵਧੀਆ ਕੰਮ।", e: "Follow-ups now happen automatically. Works better than staff." },
        ],
        t4: [
            { n: "ਵਧੀਆ ਪ੍ਰੋਡਕਟ। ਸ਼ੁਰੂ ਵਿੱਚ ਥੋੜ੍ਹਾ ਸਮਾਂ ਲੱਗਾ, ਹੁਣ ਸਭ ਠੀਕ ਹੈ।", e: "Good product. Took a little time at first, everything is fine now." },
            { n: "ਕੰਮ ਬਹੁਤ ਵਧੀਆ ਕਰਦਾ ਹੈ। ਕਈ ਵਾਰ ਮੁਸ਼ਕਿਲ ਸਵਾਲਾਂ ਲਈ ਇਨਸਾਨ ਚਾਹੀਦਾ ਹੈ।", e: "It works very well. Sometimes difficult questions need a human." },
        ],
        t3: [{ n: "ਆਮ ਸਵਾਲਾਂ ਲਈ ਠੀਕ ਹੈ।", e: "Fine for common questions." }],
    },
    {
        lang: "Bengali", native: "বাংলা",
        cities: ["Kolkata"],
        t5: [
            { n: "দোকান বন্ধ থাকলেও AI কাস্টমারদের উত্তর দেয়। একটিও lead মিস হয় না।", e: "Even when the shop is closed, the AI answers customers. Not a single lead is missed." },
            { n: "WhatsApp-এ এখন সঙ্গে সঙ্গে উত্তর পাওয়া যায়। আমাদের ব্যবসা অনেক এগিয়েছে।", e: "Replies now come instantly on WhatsApp. Our business has grown a lot." },
            { n: "AI-এর কথা এতটাই স্বাভাবিক যে কেউ বুঝতেই পারে না।", e: "The AI talks so naturally that nobody can even tell." },
        ],
        t4: [
            { n: "ভালো সার্ভিস। সেটআপে একটু সময় লেগেছিল।", e: "Good service. Setup took a little time." },
            { n: "কাজ খুব ভালো করে। কিছু কঠিন প্রশ্নে মানুষ দরকার।", e: "It works very well. Some tough questions need a human." },
        ],
        t3: [{ n: "সাধারণ প্রশ্নের জন্য ঠিক আছে।", e: "Okay for simple questions." }],
    },
    {
        lang: "Marathi", native: "मराठी",
        cities: ["Mumbai", "Pune", "Nagpur", "Nashik"],
        t5: [
            { n: "दुकान बंद असूनही AI ग्राहकांशी बोलते. आमच्या व्यवसायाला खूप फायदा झाला.", e: "Even with the shop closed, the AI talks to customers. Our business benefited a lot." },
            { n: "WhatsApp वर त्वरित उत्तरे मिळतात. booking आपोआप होतात.", e: "Instant replies on WhatsApp. Bookings happen automatically." },
            { n: "फॉलो-अप आता विसरले जात नाहीत. खूप छान सेवा.", e: "Follow-ups are never forgotten now. Very nice service." },
        ],
        t4: [
            { n: "छान प्रोडक्ट. सुरुवातीला थोडा वेळ लागला.", e: "Nice product. Took some time initially." },
            { n: "चांगले काम करते. कठीण प्रश्नांसाठी माणूस लागतो.", e: "Works well. A person is needed for tough questions." },
        ],
        t3: [{ n: "साधारण गरजांसाठी ठीक आहे.", e: "Fine for basic needs." }],
    },
    {
        lang: "Kannada", native: "ಕನ್ನಡ",
        cities: ["Bengaluru", "Mysuru"],
        t5: [
            { n: "ಅಂಗಡಿ ಮುಚ್ಚಿದರೂ AI ಗ್ರಾಹಕರ ಪ್ರಶ್ನೆಗಳಿಗೆ ಉತ್ತರಿಸುತ್ತದೆ. ತುಂಬಾ ಒಳ್ಳೆಯ ಸೇವೆ.", e: "Even when the shop is closed, the AI answers customer questions. Very good service." },
            { n: "WhatsApp ಪ್ರಶ್ನೆಗಳಿಗೆ ತಕ್ಷಣ ಉತ್ತರ ಸಿಗುತ್ತದೆ. leads ಯಾವುದೂ miss ಆಗುವುದಿಲ್ಲ.", e: "WhatsApp questions get instant answers. No leads are missed." },
            { n: "ಫಾಲో-ಅಪ್‌ಗಳು ಈಗ ಸ್ವಯಂಚಾಲಿತ. ನಮ್ಮ ಮಾರಾಟ ಹೆಚ್ಚಾಗಿದೆ.", e: "Follow-ups are now automatic. Our sales have increased." },
        ],
        t4: [
            { n: "ಒಳ್ಳೆಯ ಪ್ರೊಡಕ్ట್. ಸೆಟಪ್ ಸ್ವಲ್ಪ ಸಮಯ ತೆಗೆದುಕೊಂಡಿತು.", e: "Good product. Setup took a little time." },
            { n: "ಚೆನ್ನಾಗಿ ಕೆಲಸ ಮಾಡುತ್ತದೆ. ಕಷ್ಟದ ಪ್ರಶ್ನೆಗಳಿಗೆ ಮನುಷ್ಯ ಬೇಕು.", e: "Works well. Tough questions need a human." },
        ],
        t3: [{ n: "ಸಾಮಾನ್ಯ ಪ್ರಶ್ನೆಗಳಿಗೆ ಸಾಕು.", e: "Enough for common questions." }],
    },
    {
        lang: "Malayalam", native: "മലയാളം",
        cities: ["Kochi"],
        t5: [
            { n: "കട അടച്ചാലും AI ഉപഭോക്താക്കളോട് സംസാരിക്കും. വളരെ നല്ല സേവനം.", e: "Even when the shop is closed, the AI talks to customers. Very good service." },
            { n: "WhatsApp ചോദ്യങ്ങൾക്ക് ഉടൻ മറുപടി ലഭിക്കുന്നു. bookings കൂടി.", e: "WhatsApp questions get instant replies. Bookings increased." },
            { n: "ഫോളോ-അപ്പുകൾ ഇനി മറക്കില്ല. ഞങ്ങൾക്ക് വളരെ ഉപകാരമായി.", e: "Follow-ups are never forgotten now. Very helpful for us." },
        ],
        t4: [
            { n: "നല്ല പ്രോഡക്ട്. സെറ്റപ്പിന് കുറച്ച് സമയമെടുത്തു.", e: "Good product. Setup took some time." },
            { n: "നന്നായി പ്രവർത്തിക്കുന്നു. ബുദ്ധിമുട്ടുള്ള ചോദ്യങ്ങൾക്ക് മനുഷ്യൻ വേണം.", e: "Works well. Difficult questions need a person." },
        ],
        t3: [{ n: "സാധാരണ ചോദ്യങ്ങൾക്ക് നല്ലത്.", e: "Good for common questions." }],
    },
    {
        lang: "Hindi", native: "हिन्दी",
        cities: [
            "Delhi", "Jaipur", "Lucknow", "Indore", "Bhopal", "Kanpur", "Patna", "Agra", "Varanasi", "Meerut",
            "Jodhpur", "Raipur", "Dehradun", "Ranchi", "Jabalpur", "Gwalior", "Noida", "Gurugram", "Udaipur", "Bhilai",
            "Guwahati", "Bhubaneswar",
        ],
        t5: [
            { n: "दुकान बंद होने के बाद भी AI ग्राहकों के सवालों के जवाब देता है। एक भी lead miss नहीं होती।", e: "Even after the shop closes, the AI answers customer questions. Not a single lead is missed." },
            { n: "WhatsApp पर तुरंत जवाब मिलता है। हमारा business काफी बढ़ गया है।", e: "Instant replies on WhatsApp. Our business has grown a lot." },
            { n: "फॉलो-अप अब अपने आप होते हैं। staff से बेहतर काम।", e: "Follow-ups now happen automatically. Works better than staff." },
            { n: "Our shop was losing enquiries every evening. Since WorkAssign AI took over WhatsApp, not a single lead goes unanswered. Bookings are clearly up.", e: "", l: "English" },
            { n: "The AI calling agent sounds so natural that customers don't realise it is not a person. Best investment for our business this year.", e: "", l: "English" },
            { n: "Follow-ups used to be our weakest point. Now the AI chases every lead politely until they book. Our business finally runs on autopilot.", e: "", l: "English" },
        ],
        t4: [
            { n: "अच्छा प्रोडक्ट है। शुरुआत में थोड़ा समय लगा, अब सब ठीक चल रहा है।", e: "Good product. Took some time at first, everything runs fine now." },
            { n: "काम बहुत अच्छा करता है। कभी-कभी मुश्किल सवालों के लिए इंसान ज़रूरी है।", e: "It works very well. Sometimes a human is needed for difficult questions." },
            { n: "Works very well for us. Took a week to fine-tune the AI's answers, but now it runs smoothly. Support team is responsive.", e: "", l: "English" },
            { n: "Good product overall. The WhatsApp agent handles most queries; a few complex ones still need a human, which is fair.", e: "", l: "English" },
        ],
        t3: [
            { n: "आम सवालों के लिए ठीक है।", e: "Fine for common questions." },
            { n: "Does the job for basic enquiries. Hoping for deeper integrations in upcoming updates.", e: "", l: "English" },
        ],
    },
];

const CITY_TO_REGION = {};
REGIONS.forEach((r) => r.cities.forEach((c) => (CITY_TO_REGION[c] = r)));
const ALL_CITIES = REGIONS.flatMap((r) => r.cities);

const AVATAR_GRADIENTS = [
    "from-cyan-500 to-blue-600", "from-violet-500 to-purple-600", "from-amber-500 to-orange-600",
    "from-emerald-500 to-teal-600", "from-pink-500 to-rose-600", "from-sky-500 to-indigo-600",
];

const TOTAL = 1248;
const BASE_DATE = new Date(2026, 9, 15).getTime();

function buildReviews() {
    const rand = mulberry32(20260915);
    const out = [];
    for (let i = 0; i < TOTAL; i++) {
        const r = rand();
        const rating = r < 0.78 ? 5 : r < 0.95 ? 4 : 3;
        const biz = BUSINESSES[Math.floor(rand() * BUSINESSES.length)];
        const city = ALL_CITIES[Math.floor(rand() * ALL_CITIES.length)];
        const region = CITY_TO_REGION[city];
        const pool = rating === 5 ? region.t5 : rating === 4 ? region.t4 : region.t3;
        const text = pool[Math.floor(rand() * pool.length)];
        const name = `${FIRST[Math.floor(rand() * FIRST.length)]} ${LAST[Math.floor(rand() * LAST.length)]}`;
        const date = new Date(BASE_DATE - Math.floor(rand() * 120) * 86400000);
        out.push({
            id: i,
            name,
            city,
            biz,
            rating,
            text: text.n,
            translation: text.e,
            textLang: text.l || null,
            lang: region.lang,
            nativeLang: region.native,
            gradient: AVATAR_GRADIENTS[Math.floor(rand() * AVATAR_GRADIENTS.length)],
            date: date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }),
        });
    }
    return out;
}

const ALL_REVIEWS = buildReviews();

const Stars = ({ n }) => (
    <span className="flex gap-0.5" aria-label={`${n} out of 5 stars`}>
        {[1, 2, 3, 4, 5].map((s) => (
            <Star key={s} className={`h-3.5 w-3.5 ${s <= n ? "fill-amber-400 text-amber-400" : "text-slate-700"}`} />
        ))}
    </span>
);

const ReviewCard = ({ r }) => (
    <article className="glow-card flex h-full flex-col rounded-2xl p-5" data-testid={`review-card-${r.id}`}>
        <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br font-display text-xs font-bold text-white ${r.gradient}`}>
                {r.name.split(" ").map((w) => w[0]).join("")}
            </div>
            <div className="min-w-0">
                <p className="truncate font-display text-sm font-bold text-white">{r.name}</p>
                <p className="flex items-center gap-1 text-[11px] text-slate-500">
                    <MapPin className="h-3 w-3" /> {r.city} · {r.biz}
                </p>
            </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
            <Stars n={r.rating} />
            <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-400">
                <BadgeCheck className="h-3 w-3" /> Verified
            </span>
        </div>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-300">"{r.text}"</p>
        {r.translation && <p className="mt-2 text-[11px] italic leading-relaxed text-slate-500">{r.translation}</p>}
        <div className="mt-3 flex items-center justify-between">
            <p className="text-[11px] text-slate-600">{r.date}</p>
            <span className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[9px] font-semibold text-slate-400 notranslate" translate="no">
                <Languages className="h-2.5 w-2.5" /> {r.textLang ? "English" : `${r.nativeLang} · ${r.lang}`}
            </span>
        </div>
    </article>
);

export default function Reviews() {
    const [filter, setFilter] = useState(0); // 0 = all
    const [visible, setVisible] = useState(24);

    const stats = useMemo(() => {
        const counts = { 5: 0, 4: 0, 3: 0 };
        ALL_REVIEWS.forEach((r) => (counts[r.rating] += 1));
        const avg = ALL_REVIEWS.reduce((s, r) => s + r.rating, 0) / ALL_REVIEWS.length;
        return { counts, avg: avg.toFixed(1) };
    }, []);

    const filtered = useMemo(
        () => (filter === 0 ? ALL_REVIEWS : filter === 3 ? ALL_REVIEWS.filter((r) => r.rating <= 3) : ALL_REVIEWS.filter((r) => r.rating === filter)),
        [filter]
    );

    const FILTERS = [
        { v: 0, label: "All Reviews" },
        { v: 5, label: "5 Star" },
        { v: 4, label: "4 Star" },
        { v: 3, label: "3 Star & Below" },
    ];

    return (
        <div className="relative overflow-hidden" data-testid="reviews-page">
            <div className="glow-orb left-[20%] top-[-5%] h-[360px] w-[520px] bg-amber-500/10" />
            <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
                <Reveal className="text-center">
                    <Eyebrow>Customer Reviews</Eyebrow>
                    <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl" data-testid="reviews-heading">
                        Loved by businesses <span className="amber-gradient-text">across India</span>
                    </h1>
                    <p className="mx-auto mt-4 max-w-2xl text-slate-400">
                        Real reviews in their own languages — Gujarati, Tamil, Telugu, Punjabi, Bengali, Marathi,
                        Kannada, Malayalam and Hindi — with English translations.
                    </p>
                </Reveal>

                {/* aggregate panel */}
                <Reveal delay={0.1}>
                    <div className="glow-card mx-auto mt-12 grid max-w-4xl gap-8 rounded-3xl p-8 sm:grid-cols-[auto_1fr] sm:p-10" data-testid="reviews-summary">
                        <div className="text-center sm:text-left">
                            <p className="font-display text-6xl font-extrabold text-white" data-testid="reviews-average">{stats.avg}</p>
                            <div className="mt-2 flex justify-center sm:justify-start">
                                <Stars n={5} />
                            </div>
                            <p className="mt-2 text-sm text-slate-400">
                                <span className="font-bold text-amber-400">{TOTAL.toLocaleString("en-IN")}+</span> verified reviews
                            </p>
                        </div>
                        <div className="flex flex-col justify-center gap-2.5">
                            {[5, 4, 3].map((s) => (
                                <div key={s} className="flex items-center gap-3" data-testid={`rating-bar-${s}`}>
                                    <span className="flex w-10 items-center gap-1 text-xs text-slate-400">
                                        {s} <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                                    </span>
                                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/5">
                                        <div
                                            className="h-full rounded-full bg-gradient-to-r from-amber-500 to-orange-400"
                                            style={{ width: `${(stats.counts[s] / TOTAL) * 100}%` }}
                                        />
                                    </div>
                                    <span className="w-12 text-right text-xs text-slate-500">{Math.round((stats.counts[s] / TOTAL) * 100)}%</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </Reveal>

                {/* filters */}
                <div className="mt-10 flex flex-wrap justify-center gap-2" data-testid="review-filters">
                    {FILTERS.map((f) => (
                        <button
                            key={f.v}
                            data-testid={`review-filter-${f.v}`}
                            onClick={() => {
                                setFilter(f.v);
                                setVisible(24);
                            }}
                            className={`rounded-full px-5 py-2 text-xs font-semibold transition-colors ${
                                filter === f.v
                                    ? "border border-amber-500/40 bg-amber-500/15 text-amber-300"
                                    : "border border-white/10 bg-white/5 text-slate-400 hover:text-white"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <p className="mt-4 text-center text-xs text-slate-600" data-testid="review-count">
                    Showing {Math.min(visible, filtered.length)} of {filtered.length.toLocaleString("en-IN")} reviews
                </p>

                {/* grid */}
                <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="reviews-grid">
                    {filtered.slice(0, visible).map((r) => (
                        <ReviewCard key={r.id} r={r} />
                    ))}
                </div>

                {visible < filtered.length && (
                    <div className="mt-10 text-center">
                        <button
                            onClick={() => setVisible((v) => v + 24)}
                            data-testid="reviews-load-more"
                            className="rounded-full border border-amber-500/30 bg-amber-500/10 px-8 py-3 text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-500/20"
                        >
                            Load More Reviews
                        </button>
                    </div>
                )}

                <Reveal className="mt-16">
                    <div className="glow-card rounded-3xl p-10 text-center" data-testid="reviews-cta">
                        <MessageSquareQuote className="mx-auto h-8 w-8 text-amber-400/60" />
                        <h2 className="mt-4 font-display text-2xl font-extrabold text-white sm:text-3xl">
                            Your business could be the <span className="cyan-gradient-text">next success story</span>
                        </h2>
                        <Link
                            to="/contact"
                            data-testid="reviews-cta-button"
                            className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 px-8 py-3.5 text-sm font-semibold text-white transition-transform hover:scale-[1.03]"
                        >
                            Get Your AI Employee <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
